import Job from 'jobs/Job'
import {
  AccessStatus,
  AssessmentReviewStatus,
  AssessmentStatus,
  BookkeepingStage,
  CycleStatusType,
} from 'jobs/bookkeeping/BookkeepingProgressionInfo'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import { action, computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import isBookkeepingEnabled from 'jobs/bookkeeping/BookkeepingFeatureToggle'
import BookkeepingSubmitAssessmentDialogViewModel from '../BookkeepingSubmitAssessmentDialog/BookkeepingSubmitAssessmentDialogViewModel'
import { AlertFactory } from 'alerts/Alert'
import BookkeepingCompleteCycleDialogViewModel from '../BookkeepingCompleteCycleDialog/BookkeepingCompleteCycleDialogViewModel'
import RequestScopeChangesViewModel from '../RequestScopeChangesDialog/RequestScopeChangesViewModel'
import BookkeepingCompleteJobDialogViewModel from '../BookkeepingCompleteJobDialog/BookkeepingCompleteJobDialogViewModel'
import JobStore from 'jobs/JobStore'
import TabController from 'infra/TabController'
import BookkeepingCycleDetailsViewModel from '../BookkeepingCycleDetailsDialog/BookkeepingCycleDetailsViewModel'
import { Subscription } from 'rxjs'

export enum BookkeepingStep {
  /**
   * The job is not a bookkeeping job or is not in a valid state
   */
  NotApplicable,
  /**
   * Firm Admin Perspective: The job hasn't been picked up
   */
  AwaitingPickUp,
  /**
   * Pro and Firm Admin Perspective: The bookkeeping tracker hasn't synced
   */
  SettingUpJob,
  /**
   * Pro Perspective: The client and the admin haven't confirm the access to the bookkeeping platform
   */
  ManageAccessPending,
  /**
   * Pro Perspective: The pro has to confirm the access to the bookkeeping platform
   */
  UnmanagedConfirmAccess,
  /**
   * Firm Admin Perspective: The pro hasn't confirmed the access to the bookkeeping platform
   */
  AwaitingAccessConfirmation,
  /**
   * Pro Perspective: The pro has to start the initial assessment
   */
  ProvideInitialAssessment,
  /**
   * Firm Admin Perspective: The pro confirmed the access and will start working on the books soon
   */
  ReadyToStart,
  /**
   * Pro Perspective: The assessment has to be reviewed by an admin
   */
  AssessmentInReview,
  /**
   * Pro Perspective: The assessment has been reviewed and the pro can start the bookkeeping cycle
   */
  StartBookkeeping,
  /**
   * Pro Perspective: The cycle is starting
   */
  CycleStarting,
  /**
   * Pro and Firm Admin Perspective: The pro is working on the books
   */
  BookkeepingInProgress,
  /**
   * Pro Perspective: Start the next bookkeeping cycle
   */
  StartNextCycle,
  /**
   * Firm Admin Perspective: The pro completed a bookkeeping cycle
   */
  BookkeepingCycleComplete,
  /**
   * Pro Perspective: The pro completed all the bookkeeping items
   */
  BookkeepingCompleted,
  /**
   * Pro Perspective: The pro completed the job
   */
  JobClosed,
  /**
   * Pro Perspective
   */
  ScopeChangeRequestPending,
}

/**
 * Bookkeeping tracker card tab.
 */
export const enum BookkeepingTrackerCardTab {
  FinishedItems,
  Cycles,
}

export interface BookkeepingStepInfo {
  title: string
  description: string
  tooltip?: string
  percentDone: number
  percentPending: number
  primaryActionText?: string
  primaryAction?: () => void
  secondaryActionText?: string
  secondaryAction?: () => void
}

export default class BookkeepingProgressionViewModel {
  private _subscription: Subscription | null = null

  /**
   * Bookkeeping progression steps for the Pro
   */
  bookkeepingProSteps: { [key in BookkeepingStep]?: BookkeepingStepInfo } = {
    [BookkeepingStep.SettingUpJob]: {
      title: 'Setting up the job',
      description:
        'We are currently processing this job. Please check back in a few minutes.',
      percentDone: 0,
      percentPending: 100,
    },
    [BookkeepingStep.ManageAccessPending]: {
      title: 'Access pending',
      description: 'Customer to provide access to their books.',
      percentDone: 0,
      percentPending: 20,
    },
    [BookkeepingStep.UnmanagedConfirmAccess]: {
      title: 'Confirm you have access',
      description: `Please confirm that you can access the client's books.`,
      percentDone: 0,
      percentPending: 20,
      primaryActionText: 'Confirm Access',
      primaryAction: () => this.confirmUnmanagedPlatformAccess(),
    },
    [BookkeepingStep.ProvideInitialAssessment]: {
      title: 'Provide initial assessment',
      description: 'Review the books and provide an initial assessment.',
      percentDone: 20,
      percentPending: 20,
      primaryActionText: 'Start Assessment',
      primaryAction: () => this.bookkeepingSubmitAssessmentVM.show(),
    },
    [BookkeepingStep.AssessmentInReview]: {
      title: 'Assessment in review',
      description: 'Admins are reviewing the assessment you provided.',
      percentDone: 40,
      percentPending: 20,
    },
    [BookkeepingStep.StartBookkeeping]: {
      title: 'Start bookkeeping',
      description:
        'Start a months worth of bookkeeping. Your client will be notified that you are actively working on their books.',
      percentDone: 60,
      percentPending: 20,
      primaryActionText: 'Start Bookkeeping',
      primaryAction: () => this.startNewBookkeepingCycle(),
    },
    [BookkeepingStep.CycleStarting]: {
      title: 'Cycle starting',
      description: 'The cycle is starting, please wait.',
      percentDone: 0,
      percentPending: 100,
    },
    [BookkeepingStep.BookkeepingInProgress]: {
      title: 'Bookkeeping in progress',
      description:
        'Once you have completed the books for the month(s), complete this step.',
      percentDone: 80,
      percentPending: 20,
      primaryActionText: 'Bookkeeping Cycle Completed',
      primaryAction: () => this.bookkeepingCompleteCycleVM.show(),
    },
    [BookkeepingStep.StartNextCycle]: {
      title: 'Start next cycle',
      description:
        'Start a new month of bookkeeping after the accounting month ends. Confirm with your customer that all transactions for the month are posted. Once started, they’ll be notified you’re actively working on their books.',
      tooltip:
        'A cycle is the period at the beginning of each month when bookkeeping for the previous month takes place.',
      percentDone: 80,
      percentPending: 20,
      primaryActionText: 'Start Cycle',
      primaryAction: () => this.startNewBookkeepingCycle(),
    },
    [BookkeepingStep.BookkeepingCompleted]: {
      title: 'All bookkeeping items completed',
      description:
        'Confirm if the customer wants to extend their bookkeeping job. If yes, add a job scope change request. If not, complete the job.',
      percentDone: 100,
      percentPending: 0,
      primaryActionText: 'Complete Job',
      primaryAction: () => this.bookkeepingCompleteJobVM.show(),
      secondaryActionText: 'Add Scope Change',
      secondaryAction: () => this.requestScopeChangesVM.show(),
    },
    [BookkeepingStep.ScopeChangeRequestPending]: {
      title: 'Scope change request pending',
      description:
        'An admin is reviewing your request. Wait until the admin approves your request to start a new cycle.',
      percentDone: 80,
      percentPending: 20,
    },
    [BookkeepingStep.JobClosed]: {
      title: 'Job closed',
      description: 'Congratulations! The job is complete.',
      percentDone: 100,
      percentPending: 0,
    },
  }

  /**
   * Bookkeeping progression steps for the client
   */
  bookkeepingClientSteps: {
    [key in BookkeepingStep]?: BookkeepingStepInfo
  } = {
    [BookkeepingStep.SettingUpJob]: {
      title: 'Setting up the job',
      description:
        'We are currently processing this job. Please check back in a few minutes.',
      percentDone: 0,
      percentPending: 100,
    },
    [BookkeepingStep.ManageAccessPending]: {
      title: 'Access pending',
      description: 'Customer to provide access to their books.',
      percentDone: 0,
      percentPending: 20,
    },
    [BookkeepingStep.AwaitingPickUp]: {
      title: 'Awaiting pickup',
      description: 'A Pro will be with you shortly.',
      percentDone: 0,
      percentPending: 100,
    },
    [BookkeepingStep.AwaitingAccessConfirmation]: {
      title: 'Awaiting access confirmation',
      description: 'The Pro needs to confirm that they have access to proceed.',
      percentDone: 0,
      percentPending: 100,
    },
    [BookkeepingStep.ReadyToStart]: {
      title: 'Ready to start',
      description: 'Your Pro will soon start working on your books soon.',
      percentDone: 0,
      percentPending: 50,
    },
    [BookkeepingStep.BookkeepingInProgress]: {
      title: 'Bookkeeping in progress',
      description: 'Your Pro is currently working on your books.',
      percentDone: 50,
      percentPending: 50,
    },
    [BookkeepingStep.BookkeepingCycleComplete]: {
      title: 'Bookkeeping cycle completed',
      description:
        'Your Pro has completed the previous bookkeeping cycle. They will start the next cycle within the first few days of next month.',
      percentDone: 100,
      percentPending: 0,
    },
    [BookkeepingStep.BookkeepingCompleted]: {
      title: 'All bookkeeping items completed',
      description: 'Your Pro has completed all the bookkeeping items.',
      percentDone: 100,
      percentPending: 0,
    },
    [BookkeepingStep.ScopeChangeRequestPending]: {
      title: 'Scope change request pending',
      description:
        'An admin is reviewing a scope change request associated to your job.',
      percentDone: 80,
      percentPending: 20,
    },
    [BookkeepingStep.JobClosed]: {
      title: 'Job closed',
      description: 'The job is complete.',
      percentDone: 100,
      percentPending: 0,
    },
  }

  bookkeepingSubmitAssessmentVM =
    new BookkeepingSubmitAssessmentDialogViewModel(
      this.bookkeepingProgressionStore,
      this.job,
      this.newAlert,
    )

  /**
   * View model for the complete bookkeeping cycle.
   */
  bookkeepingCompleteCycleVM = new BookkeepingCompleteCycleDialogViewModel(
    this.newAlert,
    this.job.id,
    this.bookkeepingProgressionStore,
  )

  /**
   * View model for the bookkeeping cycle details modal.
   */
  bookkeepingCycleDetailsVM = new BookkeepingCycleDetailsViewModel(
    this.job.id,
    this.bookkeepingProgressionStore,
  )

  bookkeepingCompleteJobVM = new BookkeepingCompleteJobDialogViewModel(
    this.jobStore,
    this.job,
    this.newAlert,
  )

  /**
   * TabController for the Bookkeeping tracker card tabs.
   */
  bookkeepingTrackerCardTabController =
    new TabController<BookkeepingTrackerCardTab>(() => [
      {
        tab: BookkeepingTrackerCardTab.FinishedItems,
        title: 'Finished items',
        hasError: () => false,
      },
      {
        tab: BookkeepingTrackerCardTab.Cycles,
        title: 'Cycles',
        hasError: () => false,
      },
    ])

  constructor(
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
    private jobStore: JobStore,
    private requestScopeChangesVM: RequestScopeChangesViewModel,
    private job: Job,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Returns the bookkeeping progression info
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this.bookkeepingProgressionStore.bookkeepingProgressions.get(
      this.job.id,
    )
  }

  /**
   * Whether the user can view the Cycles tab in the Bookkeeping tracker card.
   */
  @computed
  get canViewBookkeepingCyclesTab() {
    return this.job.canViewBookkeepingCyclesTab
  }

  /**
   * Whether there are any cycles in the bookkeeping progression.
   */
  @computed
  get anyCycles() {
    const cyclesList = this.bookkeepingProgressionInfo?.cycles

    return Boolean(cyclesList?.length)
  }

  /**
   * Returns the current bookkeeping cycle
   */
  @computed
  get currentCycle() {
    return this.bookkeepingProgressionInfo?.currentCycle
  }

  /**
   * Whether the current user is a client
   */
  @computed
  get isClientCollaborator() {
    return this.job.isChampionClientCollaborator || this.job.isPartOfClientTeam
  }

  /**
   * Whether the current user is the Pro
   */
  @computed
  get isProCollaborator() {
    return this.job.isProviderCollaborator
  }

  /**
   * Whether the job is a bookkeeping job
   */
  @computed
  get isBookkeepingEnabled() {
    return this.job.jobType?.type === 'BOOKKEEPING' && isBookkeepingEnabled()
  }

  /**
   * Determines what's the current step depending on the stage, access status,
   * assessment status and the collaborator type
   */
  @computed
  get bookkeepingStep() {
    // Since we are fetching the progression info only for jobs with type bookkeeping,
    // if there is no progression yet, it means that it hasn't been created
    if (!this.bookkeepingProgressionInfo) {
      return BookkeepingStep.SettingUpJob
    }

    if (this.job.status === 'CLOSED') {
      return BookkeepingStep.JobClosed
    }

    switch (this.bookkeepingProgressionInfo.stage) {
      case BookkeepingStage.Pending:
      case BookkeepingStage.ScopeExtensionRequested:
        return BookkeepingStep.SettingUpJob
      case BookkeepingStage.ProvisioningAccess:
        switch (this.bookkeepingProgressionInfo.accessStatus) {
          case AccessStatus.NotRequested:
            if (this.isProCollaborator) {
              return BookkeepingStep.SettingUpJob
            }
            return BookkeepingStep.AwaitingPickUp
          case AccessStatus.ManagedAccessClientConnectionRequested:
          case AccessStatus.ManagedAccessClientConfirmationRequested:
          case AccessStatus.ManagedAccessAdminConfirmationRequested:
            return BookkeepingStep.ManageAccessPending
          case AccessStatus.UnmanagedAccessProviderConfirmationRequested:
            if (this.job.isProviderCollaborator) {
              return BookkeepingStep.UnmanagedConfirmAccess
            }
            return BookkeepingStep.AwaitingAccessConfirmation
          case AccessStatus.ManagedAccessConfirmed:
          case AccessStatus.UnmanagedAccessConfirmed:
            if (this.isProCollaborator) {
              return BookkeepingStep.ProvideInitialAssessment
            }
            return BookkeepingStep.ReadyToStart
          default:
            return BookkeepingStep.NotApplicable
        }
      case BookkeepingStage.AssessingBooks:
        switch (this.bookkeepingProgressionInfo.assessmentStatus) {
          case AssessmentStatus.Requested:
            if (this.job.isProviderCollaborator) {
              return BookkeepingStep.ProvideInitialAssessment
            }
            return BookkeepingStep.ReadyToStart
          case AssessmentStatus.Submitted:
            if (this.isProCollaborator) {
              switch (this.bookkeepingProgressionInfo.assessmentReviewStatus) {
                case AssessmentReviewStatus.ReviewRequested:
                  return BookkeepingStep.AssessmentInReview
                case AssessmentReviewStatus.Reviewed:
                  return BookkeepingStep.StartBookkeeping
                default:
                  return BookkeepingStep.NotApplicable
              }
            }
            return BookkeepingStep.BookkeepingInProgress
          default:
            return BookkeepingStep.NotApplicable
        }
      case BookkeepingStage.ReadyToStart:
        if (this.job.isProviderCollaborator) {
          if (!this.bookkeepingProgressionInfo.cycles.length) {
            return BookkeepingStep.StartBookkeeping
          }
          return BookkeepingStep.StartNextCycle
        }
        return BookkeepingStep.ReadyToStart
      case BookkeepingStage.CycleInProgress:
        if (this.currentCycle) {
          switch (this.currentCycle.status.type) {
            case CycleStatusType.InProgress:
              return BookkeepingStep.BookkeepingInProgress
            case CycleStatusType.Complete:
            case CycleStatusType.Canceled:
              if (this.job.isProviderCollaborator) {
                return BookkeepingStep.StartNextCycle
              }
              return BookkeepingStep.BookkeepingCycleComplete
            default:
              return BookkeepingStep.CycleStarting
          }
        }
        return BookkeepingStep.ReadyToStart
      case BookkeepingStage.Completed:
        if (this.scopeChangesSettled) {
          return BookkeepingStep.BookkeepingCompleted
        }
        return BookkeepingStep.ScopeChangeRequestPending
      default:
        return BookkeepingStep.NotApplicable
    }
  }

  /**
   * Returns the current step depending on the collaborator type
   */
  @computed
  get bookkeepingStepInfo() {
    if (this.isProCollaborator) {
      return this.bookkeepingProSteps[this.bookkeepingStep]
    }
    return this.bookkeepingClientSteps[this.bookkeepingStep]
  }

  /**
   * Whether the scope changes are settled.
   */
  @computed
  get scopeChangesSettled() {
    if (!this.job) {
      return false
    }

    return this.jobStore.scopeChangesSettled(this.job)
  }

  /**
   * Whether we can request scope changes.
   */
  @computed
  get canRequestScopeChanges() {
    if (!this.job?.canRequestScopeChanges) {
      return false
    }

    return this.scopeChangesSettled
  }

  /**
   * Bookkeeping work items
   */
  @computed
  get workItems() {
    return this.bookkeepingProgressionInfo?.workItems.filter(
      (wi) => wi.quantity > 0,
    )
  }

  /**
   * Confirms that the Pro has access to the bookkeeping platform when the
   * access method is Unmanaged
   */
  @action.bound
  async confirmUnmanagedPlatformAccess() {
    try {
      await this.bookkeepingProgressionStore.confirmUnmanagedPlatformAccess(
        this.job.id,
      )
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  }

  /**
   * Starts a new bookkeeping cycle
   */
  @action.bound
  async startNewBookkeepingCycle() {
    try {
      await this.bookkeepingProgressionStore.startNewBookkeepingCycle(
        this.job.id,
      )
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  }

  /**
   * Activates the view model
   */
  @action.bound
  activate() {
    this.bookkeepingCompleteCycleVM.activate()
    this._subscription?.unsubscribe()
    this._subscription = this.bookkeepingProgressionStore.events$.subscribe()
    void this.fetch()
  }

  /**
   * Deactivates the view model.
   */
  @action.bound
  deactivate() {
    this.bookkeepingCompleteCycleVM.deactivate()
    this._subscription?.unsubscribe()
    this._subscription = null
  }

  fetch = task.resolved(async () => {
    try {
      await this.bookkeepingProgressionStore.fetchBookkeepingProgressionInfo(
        this.job.id,
      )
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
